import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Menu,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MoreVert } from '@mui/icons-material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import PendingProfileDeleteDialog from '../components/PendingProfileDeleteDialog';

interface AdminOverview {
  total_users: number;
  total_boys: number;
  total_girls: number;
  active_users: number;
  inactive_users: number;
  premium_users: number;
  verified_profiles: number;
  pending_reviews: number;
  sent_back_reviews: number;
  approved_profiles: number;
  pending_recharges: number;
}

interface ManagedUser {
  user_id: number;
  can_delete_pending_profile?: boolean;
  profile_id?: string;
  name: string;
  email: string;
  phone: string;
  city?: string;
  review_status: string;
  is_active: boolean;
  is_premium_member: boolean;
  is_photo_verified: boolean;
  is_id_verified: boolean;
  profile_activation_status?: string;
  activation_started_at?: string;
  activation_expires_at?: string;
  reactivation_requested_at?: string;
  lead_cost_inr: number;
  wallet_balance: number;
  created_at: string;
}

interface ProfileFieldConfig {
  field_key: string;
  label: string;
  input_type: string;
  options: string[];
  is_enabled: boolean;
  is_required: boolean;
  depends_on?: string | null;
  grouped_options?: Record<string, string[]>;
}

const assistedInitialForm = {
  first_name: '', last_name: '', email: '', phone: '', password: '', date_of_birth: '', gender: 'MALE', height_cm: '', religion: '', caste: '', sub_caste: '', ekdo: '', staying_with_family: '', current_country: 'India', gotra: '', mother_tongue: '', time_of_birth: '', place_of_birth: '', marital_status: 'NEVER_MARRIED', education: '', annual_income: '', occupation: '', qualification_field: '', college_name: '', college_city: '', university_name: '', current_company_name: '', company_type: '', work_location: '', work_time: '', work_experience: '', job_type: '', remark: '', hobbies: '', city: '', state: '', family_type: '', permanent_address: '', pin_code: '', about_me: '', father_name: '', mother_name: '', father_occupation: '', mother_occupation: '', father_alive: 'YES', mother_alive: 'YES', father_mobile: '', mother_mobile: '', brother_count: '', brother_marriage_status: '', sister_count: '', sister_marriage_status: '', grandfather_name: '', grandmother_name: '', native_place: '', permanent_country: 'India', permanent_property_type: '', permanent_property_details: '', current_address: '', current_country_address: 'India', current_state: '', current_city: '', current_pin_code: '', current_property_type: '', current_property_details: '', body_type: '', complexion: '', diet: '', smoke: '', drink: '', candidate_aadhar_card: '', father_aadhar_card: '', candidate_aadhaar_number: '', parent_aadhaar_number: '', min_age: '21', max_age: '30', preference_marital_status: 'NO_PREFERENCE', location_scope: 'ANYWHERE', preferred_religion: 'NO_PREFERENCE', preferred_mother_tongue: 'NO_PREFERENCE', preferred_community: 'NO_PREFERENCE', preferred_caste: 'NO_PREFERENCE',
};

const assistedVisibleFieldKeys = new Set(Object.keys(assistedInitialForm));

const REMOVED_SENSITIVE_PROFILE_FIELDS = new Set([
  'candidate_aadhar_card',
  'father_aadhar_card',
  'candidate_aadhaar_number',
  'parent_aadhaar_number',
  'candidate_aadhaar_back',
  'parent_aadhaar_back',
]);

const fallbackSelectOptions: Partial<Record<keyof typeof assistedInitialForm, string[]>> = {
  gender: ['MALE', 'FEMALE', 'OTHER'],
  staying_with_family: ['YES', 'NO'],
  current_country: ['India', 'USA', 'Canada', 'UK', 'Australia', 'Other'],
  permanent_country: ['India', 'USA', 'Canada', 'UK', 'Australia', 'Other'],
  current_country_address: ['India', 'USA', 'Canada', 'UK', 'Australia', 'Other'],
  marital_status: ['NEVER_MARRIED', 'DIVORCED', 'WIDOWED', 'SEPARATED'],
  occupation: ['Business', 'Job'],
  father_alive: ['YES', 'NO'],
  mother_alive: ['YES', 'NO'],
  father_occupation: ['Business', 'Job'],
  mother_occupation: ['Business', 'Job', 'House Wife'],
  brother_count: ['No', '1', '2', '3', '4', '5'],
  sister_count: ['No', '1', '2', '3', '4', '5'],
  brother_marriage_status: ['One married', 'One unmarried', 'All Married', 'All Unmarried'],
  sister_marriage_status: ['One married', 'One unmarried', 'All Married', 'All Unmarried'],
  family_type: ['Joint', 'Nuclear'],
  company_type: ['Private', 'Government', 'Semi Government'],
  work_time: ['9 to 5', 'Shifts', 'Hybrid'],
  work_experience: Array.from({ length: 31 }, (_, index) => String(index)),
  job_type: ['Contract', 'Permanent', 'Freelancing'],
  permanent_property_type: ['Owned', 'Rented'],
  current_property_type: ['Owned', 'Rented'],
  permanent_property_details: ['1RK', '1BHK', '2BHK', '2.5BHK', '3BHK', '3.5BHK', '4BHK', 'Bunglow', 'Tenament'],
  current_property_details: ['1RK', '1BHK', '2BHK', '2.5BHK', '3BHK', '3.5BHK', '4BHK', 'Bunglow', 'Tenament'],
  diet: ['Vegetarian', 'Jain', 'Vegan', 'Eggetarian', 'Non Vegetarian'],
  smoke: ['No', 'Yes', 'Occasionally'],
  drink: ['No', 'Yes', 'Occasionally'],
};

const AdminUserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const isSuperAdmin = useAuthStore((state) => state.user?.role === 'SUPER_ADMIN');
  const [deleteTarget, setDeleteTarget] = useState<ManagedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [page, setPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuUser, setActionMenuUser] = useState<ManagedUser | null>(null);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [reviewFilter, setReviewFilter] = useState('ALL');
  const [activationFilter, setActivationFilter] = useState('ALL');
  const [assistedOpen, setAssistedOpen] = useState(false);
  const [assistedForm, setAssistedForm] = useState(assistedInitialForm);
  const [assistedSameAddress, setAssistedSameAddress] = useState(false);
  const [assistedPhotos, setAssistedPhotos] = useState<string[]>([]);
  const [profileFieldConfig, setProfileFieldConfig] = useState<Record<string, ProfileFieldConfig>>({});
  const [matchPreferenceOptions, setMatchPreferenceOptions] = useState<Record<string, string[]>>({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewResponse, usersResponse] = await Promise.all([
        api.get('/users/admin/overview'),
        api.get('/users/admin/users', {
          params: {
            search: searchText.trim() || undefined,
            active_status: activeFilter,
            review_status: reviewFilter,
            activation_status: activationFilter,
            page: page + 1,
            page_size: 25,
          },
        }),
      ]);
      setOverview(overviewResponse.data);
      setUsers(usersResponse.data.items || []);
      setTotalUsers(usersResponse.data.total || 0);
      if (usersResponse.data.page) setPage(usersResponse.data.page - 1);
      setSelectedUserIds([]);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load admin user controls');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeFilter, reviewFilter, activationFilter, page]);

  useEffect(() => {
    api.get('/users/public/profile-fields')
      .then((response) => {
        const config: Record<string, ProfileFieldConfig> = {};
        (response.data.items || []).forEach((item: ProfileFieldConfig) => {
          config[item.field_key] = item;
        });
        setProfileFieldConfig(config);
      })
      .catch(() => toast.error('Failed to load profile field configuration'));
    api.get('/profiles/filter-config')
      .then((response) => {
        setMatchPreferenceOptions(Object.fromEntries(
          (response.data.items || []).map((item: { field_key: string; options: string[] }) => [item.field_key, item.options || []])
        ));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchData();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const pageUserIds = users.map((item) => item.user_id);
  const allPageUsersSelected = pageUserIds.length > 0 && pageUserIds.every((id) => selectedUserIds.includes(id));

  const togglePageSelection = () => {
    setSelectedUserIds(allPageUsersSelected ? [] : pageUserIds);
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds((previous) => previous.includes(userId)
      ? previous.filter((id) => id !== userId)
      : [...previous, userId]);
  };

  const openActionMenu = (event: React.MouseEvent<HTMLElement>, item: ManagedUser) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuUser(item);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuUser(null);
  };

  const runBulkToggle = async (action: 'toggle-active' | 'toggle-premium' | 'toggle-id-verified', label: string) => {
    if (!selectedUserIds.length) return;
    try {
      setBulkWorking(true);
      await Promise.all(selectedUserIds.map((userId) => api.post(`/users/admin/users/${userId}/${action}`, {})));
      toast.success(`${label} for ${selectedUserIds.length} users`);
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || `Failed to ${label.toLowerCase()}`);
    } finally {
      setBulkWorking(false);
    }
  };

  const handleToggle = async (userId: number, action: 'toggle-active' | 'toggle-premium' | 'toggle-id-verified', successMessage: string) => {
    try {
      await api.post(`/users/admin/users/${userId}/${action}`, {});
      toast.success(successMessage);
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleLeadCostSet = async (item: ManagedUser) => {
    const input = window.prompt(`Set lead cost for ${item.name} (Points per lead):`, String(item.lead_cost_inr || 10));
    if (!input) return;

    const value = Number(input);
    if (!Number.isFinite(value) || value < 1) {
      toast.error('Enter a valid lead cost');
      return;
    }

    try {
      await api.put(`/users/admin/users/${item.user_id}/lead-cost`, { lead_cost_inr: Math.floor(value) });
      toast.success('Lead cost updated');
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update lead cost');
    }
  };

  const handleLeadCostReset = async (item: ManagedUser) => {
    try {
      await api.post(`/users/admin/users/${item.user_id}/lead-cost/reset`, {});
      toast.success('Lead cost reset to default');
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reset lead cost');
    }
  };

  const handleCreditAccount = async (item: ManagedUser) => {
    const input = window.prompt(`Add points to ${item.name} (${item.profile_id || item.email}):`, '100');
    if (!input) return;
    const points = Number(input);
    if (!Number.isFinite(points) || points < 1) {
      toast.error('Enter a valid points amount');
      return;
    }
    const note = window.prompt('Reason/note for this account credit:', 'Admin manual credit') || 'Admin manual credit';
    try {
      await api.post(`/users/admin/users/${item.user_id}/credit-account`, { points: Math.floor(points), note });
      toast.success(`${Math.floor(points)} points added to account`);
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add account points');
    }
  };

  const handleActivationApprove = async (item: ManagedUser) => {
    try {
      await api.post(`/users/admin/users/${item.user_id}/activation/approve`, {});
      toast.success('Activation approved for 1 year');
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve activation');
    }
  };

  const handleResetPassword = async (item: ManagedUser) => {
    const password = window.prompt(`Set a temporary password for ${item.name} (${item.profile_id || item.email})`);
    if (!password) return;
    if (password.length < 8) {
      toast.error('Temporary password must be at least 8 characters long');
      return;
    }
    try {
      await api.post(`/users/admin/users/${item.user_id}/reset-password`, { new_password: password });
      toast.success('Temporary password set successfully. Share it securely offline.');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reset user password');
    }
  };

  const setAssistedValue = (key: keyof typeof assistedInitialForm, value: string) => {
    setAssistedForm((current) => ({ ...current, [key]: value }));
  };

  const handleAssistedSameAddressChange = (checked: boolean) => {
    setAssistedSameAddress(checked);
    if (!checked) return;
    setAssistedForm((current) => ({
      ...current,
      current_address: current.permanent_address,
      current_country_address: current.permanent_country,
      current_state: current.state,
      current_city: current.city,
      current_pin_code: current.pin_code,
      current_property_type: current.permanent_property_type,
      current_property_details: current.permanent_property_details,
    }));
  };

  const toOptionalNumber = (value: string) => value ? Number(value) : undefined;

  const handleAssistedPhotoUpload = (files: FileList | null) => {
    if (!files?.length) return;
    const selected = Array.from(files).slice(0, 5);
    Promise.all(selected.map((file) => new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }))).then(setAssistedPhotos).catch(() => toast.error('Failed to read selected photos'));
  };

  const readImageFile = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleAssistedPrimaryPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const photo = await readImageFile(file);
      setAssistedPhotos((current) => [photo, ...current.slice(1, 5)]);
    } catch {
      toast.error('Failed to read selected profile photo');
    }
  };

  const handleAssistedAdditionalPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const photo = await readImageFile(file);
      setAssistedPhotos((current) => current.length >= 4 ? current : [...current, photo]);
    } catch {
      toast.error('Failed to read selected gallery photo');
    }
  };

  const handleAssistedDocumentUpload = (fieldName: 'candidate_aadhar_card' | 'father_aadhar_card') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAssistedValue(fieldName, String(reader.result));
    reader.onerror = () => toast.error('Failed to read selected document');
    reader.readAsDataURL(file);
  };

  const handleAssistedCreate = async () => {
    const missingConfiguredField = Object.values(profileFieldConfig).find((field) => {
      if (!field.is_enabled || !field.is_required) return false;
      if (!assistedVisibleFieldKeys.has(field.field_key)) return false;
      if (REMOVED_SENSITIVE_PROFILE_FIELDS.has(field.field_key)) return false;
      const value = assistedForm[field.field_key as keyof typeof assistedInitialForm];
      return !String(value || '').trim();
    });
    if (missingConfiguredField) {
      toast.error(`${missingConfiguredField.label} is required`);
      return;
    }
    if (!assistedForm.first_name || !assistedForm.last_name || !assistedForm.email || !assistedForm.phone || !assistedForm.password || !assistedForm.date_of_birth) {
      toast.error('Please fill name, email, phone, password, and date of birth');
      return;
    }
    if (profileFieldConfig.about_me?.is_enabled && profileFieldConfig.about_me.is_required && !assistedForm.about_me.trim()) {
      toast.error('About Me is required');
      return;
    }
    if (assistedForm.password.length < 8) {
      toast.error('Temporary password must be at least 8 characters long');
      return;
    }
    try {
      await api.post('/users/admin/assisted-profile', {
        email: assistedForm.email,
        phone: assistedForm.phone,
        password: assistedForm.password,
        first_name: assistedForm.first_name,
        last_name: assistedForm.last_name,
        date_of_birth: `${assistedForm.date_of_birth}T00:00:00`,
        gender: assistedForm.gender,
        height_cm: assistedForm.height_cm ? Number(assistedForm.height_cm) : undefined,
        religion: assistedForm.religion || undefined,
        caste: assistedForm.caste || undefined,
        sub_caste: assistedForm.sub_caste || undefined,
        marital_status: assistedForm.marital_status || undefined,
        education: assistedForm.education || undefined,
        occupation: assistedForm.occupation || undefined,
        annual_income: assistedForm.annual_income || undefined,
        city: assistedForm.city || undefined,
        state: assistedForm.state || undefined,
        about_me: assistedForm.about_me || undefined,
        photo_data_urls: assistedPhotos,
        extended_profile: {
          ekdo: assistedForm.ekdo || undefined,
          staying_with_family: assistedForm.staying_with_family || undefined,
          current_country: assistedForm.current_country || undefined,
          time_of_birth: assistedForm.time_of_birth || undefined,
          place_of_birth: assistedForm.place_of_birth || undefined,
          father_name: assistedForm.father_name || undefined,
          mother_name: assistedForm.mother_name || undefined,
          father_occupation: assistedForm.father_occupation || undefined,
          mother_occupation: assistedForm.mother_occupation || undefined,
          father_alive: assistedForm.father_alive || undefined,
          mother_alive: assistedForm.mother_alive || undefined,
          father_mobile: assistedForm.father_mobile || undefined,
          mother_mobile: assistedForm.mother_mobile || undefined,
          brother_count: toOptionalNumber(assistedForm.brother_count),
          brother_marriage_status: assistedForm.brother_marriage_status || undefined,
          sister_count: toOptionalNumber(assistedForm.sister_count),
          sister_marriage_status: assistedForm.sister_marriage_status || undefined,
          grandfather_name: assistedForm.grandfather_name || undefined,
          grandmother_name: assistedForm.grandmother_name || undefined,
          native_place: assistedForm.native_place || undefined,
          family_type: assistedForm.family_type || undefined,
          permanent_address: assistedForm.permanent_address || undefined,
          pin_code: assistedForm.pin_code || undefined,
          permanent_country: assistedForm.permanent_country || undefined,
          permanent_property_type: assistedForm.permanent_property_type || undefined,
          permanent_property_details: assistedForm.permanent_property_details || undefined,
          current_address: assistedForm.current_address || undefined,
          current_country_address: assistedForm.current_country_address || undefined,
          current_state: assistedForm.current_state || undefined,
          current_city: assistedForm.current_city || undefined,
          current_pin_code: assistedForm.current_pin_code || undefined,
          current_property_type: assistedForm.current_property_type || undefined,
          current_property_details: assistedForm.current_property_details || undefined,
          gotra: assistedForm.gotra || undefined,
          mother_tongue: assistedForm.mother_tongue || undefined,
          qualification_field: assistedForm.qualification_field || undefined,
          college_name: assistedForm.college_name || undefined,
          college_city: assistedForm.college_city || undefined,
          university_name: assistedForm.university_name || undefined,
          current_company_name: assistedForm.current_company_name || undefined,
          company_type: assistedForm.company_type || undefined,
          work_location: assistedForm.work_location || undefined,
          work_time: assistedForm.work_time || undefined,
          work_experience: assistedForm.work_experience || undefined,
          job_type: assistedForm.job_type || undefined,
          remark: assistedForm.remark || undefined,
          hobbies: assistedForm.hobbies || undefined,
          body_type: assistedForm.body_type || undefined,
          complexion: assistedForm.complexion || undefined,
          diet: assistedForm.diet || undefined,
          smoke: assistedForm.smoke || undefined,
          drink: assistedForm.drink || undefined,
        },
        match_preferences: {
          min_age: Number(assistedForm.min_age || 21),
          max_age: Number(assistedForm.max_age || 30),
          marital_status: assistedForm.preference_marital_status,
          location_scope: assistedForm.location_scope,
          religion: assistedForm.preferred_religion,
          mother_tongue: assistedForm.preferred_mother_tongue,
          preferred_community: assistedForm.preferred_community,
          preferred_caste: assistedForm.preferred_caste,
          fallback_mode: 'SAME_LANGUAGE_RELIGION',
        },
      });
      toast.success('Assisted profile created. Share the login email and temporary password securely.');
      setAssistedOpen(false);
      setAssistedForm(assistedInitialForm);
      setAssistedSameAddress(false);
      setAssistedPhotos([]);
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create assisted profile');
    }
  };

  const getStatusChipColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
    if (status === 'PENDING') return 'warning';
    if (status === 'APPROVED') return 'success';
    if (status === 'CHANGES_REQUESTED') return 'error';
    return 'default';
  };

  const assistedField = (key: keyof typeof assistedInitialForm, label: string, extra: any = {}) => (
    (() => {
      const config = profileFieldConfig[key as string];
      if (config && !config.is_enabled) return null;
      const resolvedLabel = config?.label || label;
      const required = Boolean(config?.is_required || extra.required);
      const dependsOnKey = config?.depends_on || '';
      const groupedOptions = config?.grouped_options || {};
      const options = dependsOnKey && Object.keys(groupedOptions).length > 0
        ? groupedOptions[String(assistedForm[dependsOnKey as keyof typeof assistedInitialForm] || '')] || []
        : config?.options?.length ? config.options : fallbackSelectOptions[key] || [];
      if (((config?.input_type === 'select' || config?.input_type === 'grouped_select') && options.length > 0) || fallbackSelectOptions[key]?.length) {
        return (
          <TextField
            select
            label={resolvedLabel}
            required={required}
            value={assistedForm[key]}
            onChange={(event) => setAssistedValue(key, event.target.value)}
            disabled={Boolean(dependsOnKey && Object.keys(groupedOptions).length > 0 && !assistedForm[dependsOnKey as keyof typeof assistedInitialForm])}
            helperText={dependsOnKey && Object.keys(groupedOptions).length > 0 && !assistedForm[dependsOnKey as keyof typeof assistedInitialForm] ? `Select ${dependsOnKey.replace(/_/g, ' ')} first` : extra.helperText}
            {...extra}
          >
            {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
        );
      }
      return (
        <TextField
          label={resolvedLabel}
          required={required}
          value={assistedForm[key]}
          onChange={(event) => setAssistedValue(key, event.target.value)}
          {...extra}
        />
      );
    })()
  );

  const assistedSection = (title: string, children: React.ReactNode) => (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Divider sx={{ my: 1.5 }} />
      <Typography sx={{ fontWeight: 800, color: '#7b0011', mb: 1 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>{children}</Box>
    </Box>
  );

  return (
    <AdminLayout currentPage="user-management">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          User Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Search users, inspect current state, and control account, premium, and verification access.
        </Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => setAssistedOpen(true)}>
          Create Assisted Profile
        </Button>
      </Box>

      <Dialog open={assistedOpen} onClose={() => setAssistedOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Assisted Profile</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, pt: 1 }}>
            {assistedSection('Login & Identity', <>
              {assistedField('first_name', 'Candidate Name', { required: true })}{assistedField('last_name', 'Candidate Last name', { required: true })}{assistedField('email', 'Email ID', { required: true, type: 'email' })}{assistedField('phone', 'Mobile Number', { required: true })}{assistedField('password', 'Temporary password', { required: true, type: 'password', helperText: 'User can change this later from Change Password' })}{assistedField('gender', 'Gender')}{assistedField('date_of_birth', 'Date of Birth', { required: true, type: 'date', InputLabelProps: { shrink: true } })}{assistedField('time_of_birth', 'Time of Birth', { type: 'time', InputLabelProps: { shrink: true } })}{assistedField('place_of_birth', 'Place of Birth', { required: true, placeholder: 'City or village of birth' })}{assistedField('height_cm', 'Height')}
            </>)}
            {assistedSection('Personal Details', <>
              {assistedField('religion', 'Religion')}{assistedField('caste', 'Cast')}{assistedField('gotra', 'Gotra')}{assistedField('ekdo', 'Ekdo')}{assistedField('sub_caste', 'Community')}{assistedField('mother_tongue', 'Mother Tongue')}{assistedField('marital_status', 'Marital Status')}{assistedField('staying_with_family', 'Are you staying with Family')}{assistedField('current_country', 'Currently Staying in which Country')}{assistedField('about_me', 'About Me', { multiline: true, minRows: 3 })}
            </>)}
            {assistedSection('Education Details', <>
              {assistedField('education', 'Qualification')}{assistedField('qualification_field', 'Qualification Field')}{assistedField('college_name', 'College Name')}{assistedField('college_city', 'College City')}{assistedField('university_name', 'University Name')}
            </>)}
            {assistedSection('Professional Details', <>
              {assistedField('occupation', 'Occupation')}{assistedField('current_company_name', 'Current Company Name')}{assistedField('company_type', 'Company Type')}{assistedField('work_location', 'Work Location')}{assistedField('work_time', 'Work Time')}{assistedField('work_experience', 'Work Experience')}{assistedField('job_type', 'Job Type')}{assistedField('annual_income', 'Annual Income')}{assistedField('remark', 'Remark', { multiline: true, minRows: 2 })}
            </>)}
            {assistedSection('Hobby Details', <>
              {assistedField('hobbies', 'Hobby', { multiline: true, minRows: 2, helperText: 'Add multiple hobbies using comma' })}
            </>)}
            {assistedSection('Family Details', <>
              {assistedField('father_alive', 'Father Alive')}{assistedField('father_name', 'Father Name')}{assistedField('father_occupation', 'Father Occupation')}{assistedField('father_mobile', 'Father Mobile number')}{assistedField('mother_alive', 'Mother Alive')}{assistedField('mother_name', 'Mother Name')}{assistedField('mother_occupation', 'Mother Occupation')}{assistedField('mother_mobile', 'Mother Mobile Number')}{assistedField('brother_count', 'Brother (Excluding You)')}{assistedField('brother_marriage_status', 'Brother Marriage Status')}{assistedField('sister_count', 'Sister (Excluding You)')}{assistedField('sister_marriage_status', 'Sisters Marriage Status')}{assistedField('family_type', 'Family Type')}{assistedField('grandfather_name', 'Grand Father Name')}{assistedField('grandmother_name', 'Grand Mother Name')}{assistedField('native_place', 'Native Place')}
            </>)}
            {assistedSection('Address & Property', <>
              <Typography sx={{ gridColumn: '1 / -1', fontWeight: 800 }}>Permanent Address</Typography>{assistedField('permanent_address', 'Address', { multiline: true, minRows: 2 })}{assistedField('permanent_country', 'Country')}{assistedField('state', 'State')}{assistedField('city', 'City')}{assistedField('pin_code', 'Pincode')}{assistedField('permanent_property_type', 'Property Type')}{assistedField('permanent_property_details', 'Property Details')}
              <FormControlLabel sx={{ gridColumn: '1 / -1' }} control={<Checkbox checked={assistedSameAddress} onChange={(event) => handleAssistedSameAddressChange(event.target.checked)} />} label="Current address is same as permanent address" />
              <Typography sx={{ gridColumn: '1 / -1', fontWeight: 800, mt: 1 }}>Current Address</Typography>{assistedField('current_address', 'Address', { multiline: true, minRows: 2 })}{assistedField('current_country_address', 'Country')}{assistedField('current_state', 'State')}{assistedField('current_city', 'City')}{assistedField('current_pin_code', 'Pincode')}{assistedField('current_property_type', 'Property Type')}{assistedField('current_property_details', 'Property Details')}
            </>)}
            {assistedSection('Additional Details', <>
              {assistedField('body_type', 'Body Type')}{assistedField('complexion', 'Complexion')}{assistedField('diet', 'Diet')}{assistedField('smoke', 'Smoke')}{assistedField('drink', 'Drink')}
            </>)}
            {assistedSection('Match Preferences', <>
              {assistedField('min_age', 'Preferred age from', { type: 'number', inputProps: { min: 18, max: 80 }, required: true })}{assistedField('max_age', 'Preferred age to', { type: 'number', inputProps: { min: 18, max: 80 }, required: true })}
              {([
                ['religion', 'Preferred religion', 'preferred_religion'],
                ['caste', 'Preferred caste', 'preferred_caste'],
                ['sub_caste', 'Preferred sub caste', 'preferred_community'],
              ] as const).map(([configKey, label, fieldKey]) => (
                <TextField key={fieldKey} select label={label} value={assistedForm[fieldKey]} onChange={(event) => setAssistedValue(fieldKey, event.target.value)}>
                  <MenuItem value="NO_PREFERENCE">No preference</MenuItem>
                  {(matchPreferenceOptions[configKey] || []).filter((option) => option !== 'No preference').map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
              ))}
              <TextField select label="Preferred marital status" value={assistedForm.preference_marital_status} onChange={(event) => setAssistedValue('preference_marital_status', event.target.value)}><MenuItem value="NO_PREFERENCE">No preference</MenuItem>{(matchPreferenceOptions.marital_status || []).filter((option) => option !== 'No preference').map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}</TextField>
              {([
                ['mother_tongue', 'Preferred mother tongue', 'preferred_mother_tongue'],
              ] as const).map(([configKey, label, fieldKey]) => (
                <TextField key={fieldKey} select label={label} value={assistedForm[fieldKey]} onChange={(event) => setAssistedValue(fieldKey, event.target.value)}>
                  <MenuItem value="NO_PREFERENCE">No preference</MenuItem>
                  {(matchPreferenceOptions[configKey] || []).filter((option) => option !== 'No preference').map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
              ))}
              <TextField select label="Preferred location" value={assistedForm.location_scope} onChange={(event) => setAssistedValue('location_scope', event.target.value)}><MenuItem value="SAME_CITY">Same city</MenuItem><MenuItem value="SAME_STATE">Same state</MenuItem><MenuItem value="ANYWHERE">Anywhere in India</MenuItem></TextField>
            </>)}
            {assistedSection('Profile Photos', <>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ color: '#7f7167', fontSize: '0.85rem', mb: 1.2 }}>The profile photo is used as the main photo across the platform. You may add up to 3 additional gallery photos.</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
                  <Box>
                    <Button component="label" variant="contained" fullWidth sx={{ backgroundColor: '#7b0011', '&:hover': { backgroundColor: '#5f000e' } }}>
                      {assistedPhotos[0] ? 'Replace Profile Photo' : 'Upload Profile Photo'}
                      <input hidden type="file" accept="image/*" onChange={handleAssistedPrimaryPhotoUpload} />
                    </Button>
                    {assistedPhotos[0] && <Box component="img" src={assistedPhotos[0]} alt="Profile preview" sx={{ mt: 1, width: 112, height: 112, objectFit: 'cover', borderRadius: 1, border: '2px solid #ead1b0' }} />}
                  </Box>
                  <Box>
                    <Button component="label" variant="outlined" fullWidth disabled={Math.max(assistedPhotos.length - 1, 0) >= 3} sx={{ borderColor: '#7b0011', color: '#7b0011' }}>
                      Add Gallery Photo ({Math.max(assistedPhotos.length - 1, 0)}/3)
                      <input hidden type="file" accept="image/*" onChange={handleAssistedAdditionalPhotoUpload} />
                    </Button>
                    {assistedPhotos.length > 1 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>{assistedPhotos.slice(1).map((photo, index) => <Box key={`${photo}-${index}`} sx={{ position: 'relative' }}><Box component="img" src={photo} alt={`Gallery preview ${index + 1}`} sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #e6d4be' }} /><Button size="small" onClick={() => setAssistedPhotos((current) => current.filter((_, photoIndex) => photoIndex !== index + 1))} sx={{ position: 'absolute', top: -10, right: -10, minWidth: 24, width: 24, height: 24, p: 0, borderRadius: '50%', backgroundColor: '#7b0011', color: 'white', '&:hover': { backgroundColor: '#5f000e' } }}>×</Button></Box>)}</Box>}
                  </Box>
                </Box>
              </Box>
            </>)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssistedOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssistedCreate}>Create Profile</Button>
        </DialogActions>
      </Dialog>

      {overview && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1.5, mb: 3 }}>
          {[
            ['Total Users', overview.total_users],
            ['Active Boys', overview.total_boys ?? 0],
            ['Active Girls', overview.total_girls ?? 0],
            ['Active Users', overview.active_users],
            ['Pending Reviews', overview.pending_reviews],
            ['Sent Back', overview.sent_back_reviews],
            ['Pending Recharges', overview.pending_recharges],
            ['Premium Users', overview.premium_users],
            ['Verified Profiles', overview.verified_profiles],
            ['Inactive Users', overview.inactive_users],
            ['Approved Profiles', overview.approved_profiles],
          ].map(([label, value]) => (
            <Card key={String(label)}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="textSecondary">{label}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{value}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 0.8fr 0.8fr 0.9fr 140px' }, gap: 1.5 }}>
            <TextField
              label="Search by Profile ID, Name, Email, Phone"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  fetchData();
                }
              }}
            />
            <TextField select size="small" label="Account" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
              <MenuItem value="ALL">All Accounts</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
            <TextField select size="small" label="Review Status" value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)}>
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="CHANGES_REQUESTED">Sent Back</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="INCOMPLETE">Incomplete</MenuItem>
            </TextField>
            <TextField select size="small" label="Activation" value={activationFilter} onChange={(e) => setActivationFilter(e.target.value)}>
              <MenuItem value="ALL">All Activations</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
              <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
              <MenuItem value="REACTIVATION_REQUESTED">Reactivation Requested</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={() => setSearchText('')}>Clear</Button>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Alert severity="info">No users match the current filters.</Alert>
      ) : (
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={<Checkbox checked={allPageUsersSelected} indeterminate={selectedUserIds.length > 0 && !allPageUsersSelected} onChange={togglePageSelection} />}
                label={`Select all on this page (${users.length})`}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mr: 'auto' }}>
                {selectedUserIds.length} selected
              </Typography>
              <Button size="small" variant="outlined" color="warning" disabled={!selectedUserIds.length || bulkWorking} onClick={() => runBulkToggle('toggle-active', 'Updated active status')}>
                Activate / Deactivate
              </Button>
              <Button size="small" variant="outlined" disabled={!selectedUserIds.length || bulkWorking} onClick={() => runBulkToggle('toggle-premium', 'Updated premium status')}>
                Grant / Remove Premium
              </Button>
              <Button size="small" variant="outlined" disabled={!selectedUserIds.length || bulkWorking} onClick={() => runBulkToggle('toggle-id-verified', 'Updated ID verification')}>
                Verify / Unverify ID
              </Button>
            </Stack>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ width: '100%', minWidth: 1280, tableLayout: { xs: 'auto', lg: 'fixed' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"><Checkbox checked={allPageUsersSelected} indeterminate={selectedUserIds.length > 0 && !allPageUsersSelected} onChange={togglePageSelection} /></TableCell>
                    <TableCell><strong>Profile ID</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Contact</strong></TableCell>
                    <TableCell><strong>Review</strong></TableCell>
                    <TableCell><strong>Activation</strong></TableCell>
                    <TableCell align="right"><strong>Lead Cost</strong></TableCell>
                    <TableCell align="right"><strong>Account</strong></TableCell>
                    <TableCell><strong>Flags</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((item) => (
                    <TableRow key={item.user_id}>
                      <TableCell padding="checkbox"><Checkbox checked={selectedUserIds.includes(item.user_id)} onChange={() => toggleUserSelection(item.user_id)} /></TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.profile_id || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                        <Typography variant="caption" color="textSecondary">{item.city || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.email}</Typography>
                        <Typography variant="caption" color="textSecondary">{item.phone}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={item.review_status.replace(/_/g, ' ')} color={getStatusChipColor(item.review_status)} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={(item.profile_activation_status || 'PENDING_APPROVAL').replace(/_/g, ' ')} color={item.profile_activation_status === 'ACTIVE' ? 'success' : item.profile_activation_status === 'REACTIVATION_REQUESTED' ? 'warning' : item.profile_activation_status === 'EXPIRED' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{item.lead_cost_inr || 10} Points</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{item.wallet_balance} Points</TableCell>
                      <TableCell>
                        <Stack direction="column" spacing={0.75} alignItems="flex-start">
                          <Chip size="small" label={item.is_active ? 'Active' : 'Inactive'} color={item.is_active ? 'success' : 'default'} />
                          <Chip size="small" label={item.is_premium_member ? 'Premium' : 'Standard'} color={item.is_premium_member ? 'warning' : 'default'} />
                          <Chip size="small" label={item.is_id_verified ? 'ID Verified' : 'ID Pending'} color={item.is_id_verified ? 'success' : 'default'} />
                          <Chip size="small" label={item.is_photo_verified ? 'Photo Verified' : 'Photo Pending'} color={item.is_photo_verified ? 'success' : 'default'} />
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/profile/${encodeURIComponent(item.profile_id || String(item.user_id))}`, { state: { from: '/admin/users' } })}
                          >
                            View Profile
                          </Button>
                          <IconButton aria-label={`More actions for ${item.name}`} title="More actions" onClick={(event) => openActionMenu(event, item)}>
                            <MoreVert />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalUsers}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={25}
              rowsPerPageOptions={[25]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
            <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={closeActionMenu}>
              {actionMenuUser && (
                <>
                  <MenuItem onClick={() => { void handleToggle(actionMenuUser.user_id, 'toggle-active', actionMenuUser.is_active ? 'User deactivated' : 'User activated'); closeActionMenu(); }}>
                    {actionMenuUser.is_active ? 'Deactivate' : 'Activate'}
                  </MenuItem>
                  <MenuItem onClick={() => { void handleToggle(actionMenuUser.user_id, 'toggle-premium', actionMenuUser.is_premium_member ? 'Premium removed' : 'Premium granted'); closeActionMenu(); }}>
                    {actionMenuUser.is_premium_member ? 'Remove Premium' : 'Grant Premium'}
                  </MenuItem>
                  <MenuItem onClick={() => { void handleToggle(actionMenuUser.user_id, 'toggle-id-verified', actionMenuUser.is_id_verified ? 'ID verification removed' : 'ID verified'); closeActionMenu(); }}>
                    {actionMenuUser.is_id_verified ? 'Unverify ID' : 'Verify ID'}
                  </MenuItem>
                  <MenuItem onClick={() => { void handleLeadCostSet(actionMenuUser); closeActionMenu(); }}>Set Lead Cost</MenuItem>
                  <MenuItem onClick={() => { void handleLeadCostReset(actionMenuUser); closeActionMenu(); }}>Reset Lead Cost</MenuItem>
                  <MenuItem onClick={() => { void handleCreditAccount(actionMenuUser); closeActionMenu(); }}>Add Points</MenuItem>
                  <MenuItem onClick={() => { void handleResetPassword(actionMenuUser); closeActionMenu(); }}>Reset Password</MenuItem>
                  {(actionMenuUser.profile_activation_status === 'EXPIRED' || actionMenuUser.profile_activation_status === 'REACTIVATION_REQUESTED' || actionMenuUser.profile_activation_status === 'PENDING_APPROVAL') && (
                    <MenuItem onClick={() => { void handleActivationApprove(actionMenuUser); closeActionMenu(); }}>Activate 1 Year</MenuItem>
                  )}
                  <MenuItem onClick={() => { navigate(`/admin/user-control-center?userId=${encodeURIComponent(actionMenuUser.profile_id || String(actionMenuUser.user_id))}`); closeActionMenu(); }}>
                    Control Center
                  </MenuItem>
                  {isSuperAdmin && actionMenuUser.can_delete_pending_profile && (
                    <MenuItem sx={{ color: 'error.main' }} onClick={() => { setDeleteTarget(actionMenuUser); closeActionMenu(); }}>Delete Profile Permanently</MenuItem>
                  )}
                </>
              )}
            </Menu>
          </CardContent>
        </Card>
      )}
      {deleteTarget && <PendingProfileDeleteDialog key={deleteTarget.user_id} profile={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={fetchData} />}
    </AdminLayout>
  );
};

export default AdminUserManagementPage;